"use client";
import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { Box } from '@mui/material';
import { Header, Sidebar } from '@/components';
import SpinnerOverlay from '@/components/SpinnerOverlay';
import { ToastNotification, ModalToastNotification } from "@int-traderjoes/ui-components-library";
import './layout.css';
import "@int-traderjoes/ui-components-library/dist/style.css";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { isHiddenScrollbarRoute } from "@/utils/hiddenScrollbarRoutes";

const Layout = ({
  hideSidebar = false,
  isAuthorisedShell = false,
  children,
}: Readonly<{
  hideSidebar?: boolean;
  /** True when rendered from `(authorised)` route group layouts. */
  isAuthorisedShell?: boolean;
  children: React.ReactNode;
}>) => {
  // Initialize state based on actual window size immediately (if available)
  const getInitialMobileState = (): boolean => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  };

  const getInitialOpenState = (): boolean => {
    if (typeof window === 'undefined') return false;
    const screenSize = window.innerWidth;
    const isTabletScreen = screenSize >= 768 && screenSize < 1024;
    return !isTabletScreen;
  };

  // Use a mounted state to prevent SSR hydration mismatch
  const [mounted, setMounted] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false); // Safe default for SSR
  const [isMobile, setIsMobile] = useState<boolean>(false); // Safe default for SSR

  const { isSidebarVisible, setIsSidebarVisible, isLoading} = useGlobalStateContext();
  const pathname = usePathname();
  const hideScrollbar = isHiddenScrollbarRoute(pathname);

  useEffect(() => {
    // Mark as mounted - this ensures we're on the client side
    setMounted(true);
    
    // Set initial state based on actual window size
    const screenSize = window.innerWidth;
    const isMobileScreen = screenSize < 768;
    const isTabletScreen = screenSize >= 768 && screenSize < 1024;
    
    setIsMobile(isMobileScreen);
    setOpen(!isTabletScreen);
    setIsSidebarVisible(!isMobileScreen);
    
    // Comprehensive function to check and update screen size states
    const updateScreenSize = () => {
      if (typeof window === 'undefined') return;
      
      const screenSize = window.innerWidth;
      
      // Explicit mobile check: strictly less than 768px
      const isMobileScreen = screenSize < 768;
      
      // Explicit tablet check: 768px to 1023px (less than 1024px)
      const isTabletScreen = screenSize >= 768 && screenSize < 1024;
      
      // Update mobile state only if it changed
      setIsMobile(prev => {
        if (prev !== isMobileScreen) {
          return isMobileScreen;
        }
        return prev;
      });
  
      // Open sidebar for tablet screens and larger (desktop)
      setOpen(prev => {
        const newOpen = !isTabletScreen;
        if (prev !== newOpen) {
          return newOpen;
        }
        return prev;
      });
  
      // Toggle sidebar visibility based on screen size
      setIsSidebarVisible(!isMobileScreen);
    };

    // Debounce function to prevent rapid state updates
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateScreenSize();
      }, 100); // 100ms debounce
    };
  
    // Add resize listener
    window.addEventListener('resize', handleResize);
  
    // Also listen to orientation changes for mobile devices
    const handleOrientationChange = () => {
      // Small delay to ensure window dimensions are updated
      setTimeout(updateScreenSize, 100);
    };
    window.addEventListener('orientationchange', handleOrientationChange);
  
    // Cleanup listeners
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [setIsSidebarVisible]);

  const sidebarState = open ? 'sidebar-open' : 'sidebar-collapsed';
  const hideSidebarClass=()=>{
    if(hideSidebar) return 'no-sidebar';
    if(isMobile) return 'no-sidebar';
    return sidebarState;
    
  };

  return (
    <>
    {isLoading && <SpinnerOverlay />}
    <Box className="flex flex-row">
      {isSidebarVisible && !hideSidebar && (
        <aside className='z-50'>
          <Sidebar open={open} setOpen={setOpen} isMobile={isMobile} />
        </aside>
      )}
      <main className={`flex flex-col w-full content ${hideSidebarClass()}`}>
        <Header showSearch={!isMobile} isAuthorisedShell={isAuthorisedShell} />
        <section
          className={`${hideScrollbar ? "layout-section" : "layout-visible-scrollbar"} ${hideSidebar ? "flex justify-center" : ""}`}
        >
          {children}
        </section>
      </main>
      <ToastNotification />
      <ModalToastNotification />
    </Box>
    </>
  );
};

export default Layout;
